import {
  MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH,
  MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH,
  MEDREFILL_TOPUP_CLINICS_PATH,
} from '@shared/constants/paths';
import {
  useGetClinicListQuery,
  useGetCollectionListQuery,
  useGetLocationListQuery,
  useGetMedicationListQuery,
  useGetMedRefillInstitutionsQuery,
  useGetMedRefillPaymentModesQuery,
  useGetPublicClinicListQuery,
  useGetPublicCollectionListQuery,
  useGetPublicLocationListQuery,
  useGetPublicMedicationListQuery,
  useGetPublicMedRefillInstitutionsQuery,
  useGetPublicMedRefillPaymentModesQuery,
  useLazyGetLocationListQuery,
  useLazyGetPublicLocationListQuery,
  useSubmitMedRefillRequestMutation,
  useSubmitPublicMedRefillRequestMutation,
} from '@shared/services/medrefill';

export const REFILL_MEDICATION_OPTIONS = {
  customiseQuantity: 'REFILL_MEDICATION_OPTION_4',
  remainingBalance: 'REFILL_MEDICATION_OPTION_1',
  selectedMedication: 'REFILL_MEDICATION_OPTION_3',
  specificDuration: 'REFILL_MEDICATION_OPTION_2',
};

export const MEDICATION_MEASUREMENTS = {
  duration: 'DURATION',
  uom: 'UOM',
};

export const ACCEPTABLE_FILE_TYPES = ['image/jpeg', 'image/png'];

export const DESIRED_DURATION_ORDER = ['Day(s)', 'Week(s)', 'Month(s)'];
export const MEASUREMENT_DAY = 'DAY';
export const MEASUREMENT_WEEK = 'WEEK';
export const MEASUREMENT_MONTH = 'MONTH';
export const MAX_MEASUREMENT_LIMIT: Record<string, number> = {
  DAY: 365,
  MONTH: 12,
  WEEK: 52,
};

export const COLLECTION_CODES = {
  delivery: 'HOME_DELIVERY', // recieve in person
  externalLocker: 'EXTERNAL_LOCKER', // from locker near me
  externalPharmacy: 'EXTERNAL_PHARMACY', //  from external pharmacy
  institutionLocker: 'INSTITUTION_LOCKER', // from institution locker
  institutionPharmacy: 'INSTITUTION_PHARMACY', // from pharmacy
  leaveAtDoorstep: 'LEAVE_AT_DOORSTEP', // leave at doorstep
};

export const MEDREFILL_SOURCE = {
  maxcare: 'Maxcare',
  nhips: 'NHIPS',
  phis: 'PHIS',
  ppim: 'PPIM',
};

export const SUMMARY_REMARKS_MAX_LENGTH = {
  default: 2000,
  legacy: 200,
  nhips: 1000,
};

export const PAYMENT_REMARKS_MAX_LENGTH = {
  default: 2000,
  maxcare: 200,
  nhips: 1000,
  phis: 400,
  ppim: 150,
};

export const STREET_MAX_LENGTH = {
  default: 200,
  maxcare: 100,
  nhips: 200,
  phis: 200,
  ppim: 100,
};

export const EMAIL_MAX_LENGTH = {
  default: 100,
  maxcare: 50,
  nhips: 50,
  phis: 100,
  ppim: 100,
};

export const MEDICATIONS_CHECKBOX = {
  all: 'ALL',
  date: 'DATE',
  medication: 'MEDICATION',
};

export const SORT_TYPES = {
  az: 'az',
  za: 'za',
};

export const NA_TYPE = 'NA';

export const REQUESTOR_TYPE = {
  careGiver: 'CAREGIVER',
  other: 'SOMEONE_ELSE',
  self: 'SELF',
};

export const JPEG_FORMAT = 'jpeg';
export const MAX_IMAGE_WIDTH = 2000;
export const MAX_IMAGE_HEIGHT = 2000;
export const RESIZE_IMAGE_QUALITY = 80;
export const MAX_UPLOAD_SIZE_MB = 3;

export const MEDREFILL_INSTITUTIONS_CONFIG = {
  public: {
    getMedRefillInstitutionsQuery: useGetPublicMedRefillInstitutionsQuery,
  },
  singpass: {
    getMedRefillInstitutionsQuery: useGetMedRefillInstitutionsQuery,
  },
};

export const MEDREFILL_CLINICS_CONFIG = {
  public: {
    getClinicsQuery: useGetPublicClinicListQuery,
  },
  singpass: {
    getClinicsQuery: useGetClinicListQuery,
  },
};

export const MEDREFILL_MEDICATIONLIST_CONFIG = {
  public: {
    getMedicationListQuery: useGetPublicMedicationListQuery,
  },
  singpass: {
    getMedicationListQuery: useGetMedicationListQuery,
  },
};

export const MEDREFILL_COLLECTIONLIST_CONFIG = {
  public: {
    getCollectionListQuery: useGetPublicCollectionListQuery,
  },
  singpass: {
    getCollectionListQuery: useGetCollectionListQuery,
  },
};

export const MEDREFILL_LOCATIONLIST_CONFIG = {
  public: {
    getLazyLocationListQuery: useLazyGetPublicLocationListQuery,
    getLocationListQuery: useGetPublicLocationListQuery,
  },
  singpass: {
    getLazyLocationListQuery: useLazyGetLocationListQuery,
    getLocationListQuery: useGetLocationListQuery,
  },
};

export const MEDREFILL_PAYMENTMODELIST_CONFIG = {
  public: {
    getPaymentModeListQuery: useGetPublicMedRefillPaymentModesQuery,
  },
  singpass: {
    getPaymentModeListQuery: useGetMedRefillPaymentModesQuery,
  },
};

export const MEDREFILL_SUBMITREQUEST_CONFIG = {
  public: {
    submitMedrefillRequestMutation: useSubmitPublicMedRefillRequestMutation,
  },
  singpass: {
    submitMedrefillRequestMutation: useSubmitMedRefillRequestMutation,
  },
};

export const MEDREFILL_SUPPORT_URL = 'https://support.healthhub.sg/hc/en-us/categories/15434973132057-Medications';

export const MEDREFILL_PHARMACY_CONTACT_URL =
  'https://support.healthhub.sg/hc/en-us/articles/59087776745625-How-do-I-contact-my-institution-about-my-medication-refill-request';

export const MEDREFILL_OTHERS_PATH = '/others';

export const SUMMARY_DATA_KEYS = {
  collection: 'collection',
  integratedOptions: 'integratedOptions',
  options: 'options',
  particulars: 'particulars',
  payment: 'payment',
};

export const VIRTUAL_CONSULT = 'vc';

export const SOURCE_ERROR_CODES = ['PHIS', 'MC', 'PPIM', 'NHIPS'];

export const MEDREFILL_TERMS_AND_CONDITIONS = 'MedRefill.TermsAndConditions';

export const NHIPS_UNAVAILABLE_ERROR_CODE = 'MED0036';

const NHIPS_MISSING_COLLECTION_MODE = 'MED0037';

const NHIPS_MISSING_LOCATION = 'MED0038';

const NHIPS_MISSING_DATE = 'MED0039';

const NHIPS_MISSING_TIMESLOT = 'MED0040';

export const NHIPS_CHAIN_ERROR_TO_MESSAGE_KEY: Record<string, string> = {
  [NHIPS_MISSING_COLLECTION_MODE]: 'content.selectAnotherCollectionMethod',
  [NHIPS_MISSING_LOCATION]: 'content.selectAnotherLocation',
  [NHIPS_MISSING_DATE]: 'content.selectAnotherDate',
  [NHIPS_MISSING_TIMESLOT]: 'content.selectAnotherTime',
};

export const NHIPS_RESELECT_DATE = 'NHIPS0021';

export const NHIPS_RESLECT_COLLECTION_MODE = 'NHIPS0022';

export const ERROR_CODE_TO_BUTTON_KEY_MAPPING: Record<string, string> = {
  [NHIPS_RESELECT_DATE]: 'button.reselectDate',
  [NHIPS_RESLECT_COLLECTION_MODE]: 'button.reselectCollectionMode',
};

export const NHIPS_UNUSED_ID = 0;

export const MEDREFILL_WHITELISTED_PATH = [
  MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH,
  MEDREFILL_TOPUP_CLINICS_PATH,
  MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH,
];

export const POSITIVE_QUANTITY_REGEX = /^[1-9]\d*$/;
